<template>
    <span v-if="rowvalue?.Code || rowvalue.DisableTwoFactor">
        <strong
            class="text-info"
            v-if="!rowvalue.DisableTwoFactor"
            >{{ rowvalue?.Code }}
        </strong>
        <span
            class="text-warning"
            v-else
            >{{ $tm('account.user2fadisableduntil') }}</span
        >
        <DateComp
            class="ms-3 text-muted small"
            :value="rowvalue?.Expires"
            :monospace="false"
            format="DATETIME"
        />
    </span>

    <span v-else>
        <GridButton
            :row="row"
            icon="fa-xmark"
            text="general.disable"
            @click="disable2FA()"
        />
    </span>
</template>

<script setup lang="ts">
import { DateComp, GridButton, InputDateTime } from '@/components';
import { useDialog } from '@/composeables';
import { UserDao } from '@/dao';
import { Participant, User2FACode } from '@/objects/generated';
import { useToastStore } from '@/stores';
import { addTimeToDate } from '@/utilities/dateutils';
import { ref } from 'vue';

const props = defineProps<{
    value: User2FACode | null;
    row: Participant;
}>();

const rowvalue = ref<User2FACode>(props.value ?? User2FACode.FromJsValues({}));

const disable2FA = () => {
    // Show modal with DateTime input and warning
    // Set the default date to the end of the next day
    let in24hrs = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
    const disableUntil = ref<Date>(addTimeToDate(new Date('2001-01-01T23:59:59'), new Date(in24hrs)) ?? in24hrs);

    useDialog().SaveCancel({
        title: 'account.disable2fauntil',
        body: InputDateTime,
        vmodel: disableUntil,
        saveCallback: async () => {
            // Call the API to disable 2FA
            if (!disableUntil.value) {
                return false;
            }

            // Save the data to the backend
            try {
                await UserDao.DisableUser2FA(props.row.ID, disableUntil.value);

                rowvalue.value.DisableTwoFactor = true;
                rowvalue.value.Expires = disableUntil.value;
            } catch (e) {
                useToastStore().addError((e as Error).message);
            }

            return true;
        },
        componentProps: { format: 'DATETIME' },
    });
};
</script>
